import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save, Trash2, ExternalLink, Copy, X } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import ImagePicker from '../../ondemand-library/content-catalog/ImagePicker'
import AdBuilderPreview from './AdBuilderPreview'
import AdBuilderStatsCard from './AdBuilderStatsCard'
import '../sponsor-manager/SponsorManager.css'
import './AdBuilder.css'

// Public render is served by the watch worker — see the plan at
// ~/.claude/plans/i-have-a-970x250-dynamic-lynx.md.
const WATCH_BASE = 'https://watch.tenband.tv'

// `/sponsors/ad-builder/new`   → create
// `/sponsors/ad-builder/:slug` → edit
//
// Editor for a single ad-builder config. The right pane is a live
// preview that re-renders whenever the form changes (debounced on
// VAST fetches by the preview's own effect dependency on zone_id).

const EMPTY = {
  slug: '',
  name: '',
  mode: 'vast',
  zone_id: '',
  oid: '28740',
  rotation_seconds: 30,
  bg_color: '#000000',
  fallback_image_url: '',
  click_target: '_blank',
  vmix_enabled: false,
  vmix_host: '127.0.0.1:8088',
  vmix_overlay: 3,
  vmix_title: '',
  extra_params_json: '',
}

export default function AdBuilderEditor() {
  const { api } = useAuth()
  const navigate = useNavigate()
  const { slug: routeSlug } = useParams()
  const isNew = !routeSlug || routeSlug === 'new'

  const [cfg, setCfg] = useState(EMPTY)
  const [zones, setZones] = useState([])
  const [zoneFilter, setZoneFilter] = useState('')
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState('')

  const loadConfig = useCallback(async () => {
    if (isNew) return
    setLoading(true)
    try {
      const data = await api(`/ad-builder/${routeSlug}`)
      // Server returns nulls for unset optional fields; coerce to '' for
      // the controlled inputs so React doesn't warn.
      const c = data.config || {}
      setCfg({
        ...EMPTY,
        ...c,
        fallback_image_url: c.fallback_image_url ?? '',
        vmix_host:          c.vmix_host          ?? '127.0.0.1:8088',
        vmix_overlay:       c.vmix_overlay       ?? 3,
        vmix_title:         c.vmix_title         ?? '',
        extra_params_json:  c.extra_params_json  ?? '',
      })
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setLoading(false)
  }, [api, routeSlug, isNew])

  useEffect(() => { loadConfig() }, [loadConfig])

  // Zones come from sponsor-api (vader proxies via service binding).
  // active-only is what AdsList uses too — same expectation.
  useEffect(() => {
    api('/sponsors/zones?status=active')
      .then(d => setZones(d.zones || []))
      .catch(() => {})
  }, [api])

  function set(key, val) {
    setCfg(prev => ({ ...prev, [key]: val }))
  }

  async function handleSave(e) {
    e?.preventDefault()
    setStatus('')
    if (!cfg.name.trim()) return setStatus('Error: Name is required')
    if (!cfg.zone_id) return setStatus('Error: Pick a zone')
    if (isNew && !/^[a-z0-9][a-z0-9-]{0,63}$/.test(cfg.slug)) {
      return setStatus('Error: Slug must be lowercase letters, digits, and dashes')
    }
    setSaving(true)
    try {
      const payload = {
        ...cfg,
        rotation_seconds: Number(cfg.rotation_seconds),
        vmix_overlay: cfg.vmix_overlay === '' ? null : Number(cfg.vmix_overlay),
      }
      if (isNew) {
        const res = await api('/ad-builder', { method: 'POST', body: JSON.stringify(payload) })
        if (res?.error) throw new Error(res.error)
        setStatus('Created')
        navigate(`/sponsors/ad-builder/${payload.slug}`)
      } else {
        // Strip slug — server treats it as PK and rejects updates.
        // Strip created_at / updated_at — server-managed.
        // eslint-disable-next-line no-unused-vars
        const { slug, created_at, updated_at, ...patch } = payload
        const res = await api(`/ad-builder/${routeSlug}`, { method: 'PATCH', body: JSON.stringify(patch) })
        if (res?.error) throw new Error(res.error)
        setStatus('Saved')
      }
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setSaving(false)
  }

  async function handleDelete() {
    if (isNew) return
    if (!window.confirm(`Delete ad-builder config "${routeSlug}"? The public render URL will return 404 after this.`)) return
    setSaving(true)
    try {
      await api(`/ad-builder/${routeSlug}`, { method: 'DELETE' })
      navigate('/sponsors/ad-builder')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
      setSaving(false)
    }
  }

  function renderUrl() {
    return cfg.slug ? `${WATCH_BASE}/ad-builder/${cfg.slug}` : ''
  }

  async function copyUrl() {
    try {
      await navigator.clipboard.writeText(renderUrl())
      setStatus('Render URL copied')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  const filteredZones = zoneFilter
    ? zones.filter(z => {
        const q = zoneFilter.toLowerCase()
        const name = String(z.name ?? z.Name ?? '').toLowerCase()
        const id = String(z.id ?? z.ZoneId ?? '').toLowerCase()
        return name.includes(q) || id.includes(q)
      })
    : zones

  if (loading) return <div className="sponsors-page"><div className="muted">Loading…</div></div>

  return (
    <div className="sponsors-page">
      <div className="sponsors-header">
        <div className="sponsors-header-left">
          <button className="sponsors-refresh-btn" onClick={() => navigate('/sponsors/ad-builder')}>
            <ArrowLeft size={14} /> Back
          </button>
          <h1>{isNew ? 'New ad page' : cfg.name || cfg.slug}</h1>
        </div>
        <div className="sponsors-header-right">
          {!isNew && (
            <>
              <a
                className="sponsors-refresh-btn"
                href={renderUrl()}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink size={14} /> Open render
              </a>
              <button className="sponsors-refresh-btn" onClick={handleDelete} disabled={saving}>
                <Trash2 size={14} /> Delete
              </button>
            </>
          )}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={14} /> {saving ? 'Saving…' : (isNew ? 'Create' : 'Save')}
          </button>
        </div>
      </div>

      {status && (
        <div className={`sponsors-status ${status.startsWith('Error') ? 'error' : 'success'}`}>
          <span>{status}</span>
          <button onClick={() => setStatus('')}><X size={14} /></button>
        </div>
      )}

      <div className="ad-builder-grid">
        <form onSubmit={handleSave}>
          <div className="ad-builder-form-card">
            <h3>Identity</h3>
            <div className="ad-builder-field">
              <label>Slug {!isNew && <span className="muted">(read-only)</span>}</label>
              <input
                type="text"
                value={cfg.slug}
                disabled={!isNew}
                onChange={e => set('slug', e.target.value.toLowerCase())}
                placeholder="e.g. pihl-pregame"
                pattern="[a-z0-9][a-z0-9-]{0,63}"
                required
              />
            </div>
            <div className="ad-builder-field">
              <label>Name</label>
              <input
                type="text"
                value={cfg.name}
                onChange={e => set('name', e.target.value)}
                placeholder="e.g. PIHL Pregame Web Ad"
                required
              />
            </div>
          </div>

          <div className="ad-builder-form-card" style={{ marginTop: 14 }}>
            <h3>Ad source</h3>
            <div className="ad-builder-field">
              <label>Mode</label>
              <div className="ad-builder-radio-row">
                <label>
                  <input type="radio" name="mode" value="vast" checked={cfg.mode === 'vast'}
                    onChange={() => set('mode', 'vast')} />
                  VAST tag (parsed, static image)
                </label>
                <label>
                  <input type="radio" name="mode" value="js" checked={cfg.mode === 'js'}
                    onChange={() => set('mode', 'js')} />
                  JS tag (Revive iframe)
                </label>
              </div>
            </div>
            <div className="ad-builder-field">
              <label>Zone</label>
              <input
                type="text"
                placeholder="Filter zones by name or id…"
                value={zoneFilter}
                onChange={e => setZoneFilter(e.target.value)}
                style={{ marginBottom: 6 }}
              />
              <select
                value={cfg.zone_id}
                onChange={e => set('zone_id', e.target.value)}
                required
              >
                <option value="">— Pick a zone —</option>
                {filteredZones.map(z => {
                  const zid = String(z.id ?? z.ZoneId)
                  const zname = z.name ?? z.Name ?? `Zone ${zid}`
                  return <option key={zid} value={zid}>{zname} (id {zid})</option>
                })}
              </select>
            </div>
            <div className="ad-builder-row">
              <div className="ad-builder-field">
                <label>OID</label>
                <input type="text" value={cfg.oid} onChange={e => set('oid', e.target.value)} />
              </div>
              <div className="ad-builder-field">
                <label>Rotation (seconds)</label>
                <input
                  type="number" min="1" max="3600"
                  value={cfg.rotation_seconds}
                  onChange={e => set('rotation_seconds', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="ad-builder-form-card" style={{ marginTop: 14 }}>
            <h3>Appearance</h3>
            <div className="ad-builder-row">
              <div className="ad-builder-field">
                <label>Background color</label>
                <input type="color" value={cfg.bg_color}
                  onChange={e => set('bg_color', e.target.value)} />
              </div>
              <div className="ad-builder-field">
                <label>Click target</label>
                <select value={cfg.click_target}
                  onChange={e => set('click_target', e.target.value)}>
                  <option value="_blank">_blank (new tab)</option>
                  <option value="_self">_self (same tab)</option>
                </select>
              </div>
            </div>
            <div className="ad-builder-field">
              <label>Fallback image (shown when VAST fails)</label>
              <ImagePicker
                value={cfg.fallback_image_url}
                onChange={v => set('fallback_image_url', v)}
                placeholder="https://… or browse Cloudflare Images"
              />
            </div>
          </div>

          <details className="ad-builder-vmix-box" open={!!cfg.vmix_enabled}>
            <summary>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={!!cfg.vmix_enabled}
                  onChange={e => set('vmix_enabled', e.target.checked)}
                  onClick={e => e.stopPropagation()}
                />
                vMix overlay polling
              </label>
            </summary>
            <div className="ad-builder-row">
              <div className="ad-builder-field">
                <label>vMix host</label>
                <input type="text" value={cfg.vmix_host}
                  onChange={e => set('vmix_host', e.target.value)}
                  placeholder="127.0.0.1:8088" />
              </div>
              <div className="ad-builder-field">
                <label>Overlay channel</label>
                <input type="number" min="1" max="8"
                  value={cfg.vmix_overlay}
                  onChange={e => set('vmix_overlay', e.target.value)} />
              </div>
            </div>
            <div className="ad-builder-field">
              <label>Input title to watch</label>
              <input type="text" value={cfg.vmix_title}
                onChange={e => set('vmix_title', e.target.value)}
                placeholder="e.g. tbtv-web-ads.gtzip" />
            </div>
          </details>

          <div className="ad-builder-form-card" style={{ marginTop: 14 }}>
            <h3>Advanced</h3>
            <div className="ad-builder-field">
              <label>Extra params (JSON, escape hatch)</label>
              <textarea
                value={cfg.extra_params_json}
                onChange={e => set('extra_params_json', e.target.value)}
                placeholder='{"customField": "value"}'
              />
              <div className="muted" style={{ fontSize: '0.72rem', marginTop: 4 }}>
                Persisted with the config; not currently consumed by the renderer. Stored for future wiring.
              </div>
            </div>
          </div>
        </form>

        <div>
          {!isNew && cfg.slug && (
            <AdBuilderStatsCard slug={cfg.slug} zoneId={cfg.zone_id} />
          )}
          <AdBuilderPreview config={cfg} />
          {!isNew && cfg.slug && (
            <div className="ad-builder-render-url">
              <input type="text" readOnly value={renderUrl()} onFocus={e => e.target.select()} />
              <button className="sponsors-refresh-btn" onClick={copyUrl} title="Copy">
                <Copy size={13} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
